import { useEffect, useState } from "react";

function useAfterLoad(timeout = 2000) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId = 0;
    let idleId = 0;

    const enable = () => {
      if (!cancelled) setReady(true);
    };

    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(enable, { timeout });
      } else {
        timeoutId = window.setTimeout(enable, 1);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      if (idleId && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [timeout]);

  return ready;
}

type NetworkInformation = {
  saveData?: boolean;
  effectiveType?: string;
};

function shouldLoadHeroVideo() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  // Poster image covers phones; skip the video bytes on small viewports.
  if (window.matchMedia("(max-width: 767px)").matches) return false;
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (connection?.saveData) return false;
  if (connection?.effectiveType === "slow-2g" || connection?.effectiveType === "2g") return false;
  return true;
}

export function DeferredHeroVideo({ webm, mp4 }: { webm: string; mp4: string }) {
  const afterLoad = useAfterLoad();
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    setAllowed(shouldLoadHeroVideo());
  }, []);

  if (!afterLoad || !allowed) return null;

  return (
    <video
      className="absolute inset-0 h-full w-full object-cover"
      autoPlay
      muted
      loop
      playsInline
      preload="none"
    >
      <source src={webm} type="video/webm" />
      <source src={mp4} type="video/mp4" />
    </video>
  );
}

export function DeferredHeroGlow() {
  const shouldLoad = useAfterLoad();

  if (!shouldLoad) return null;

  return (
    <div
      aria-hidden
      className="hero-glow absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-accent/30 blur-3xl"
    />
  );
}
